"use client";

import { useEffect, useRef } from "react";

type PublicNavbarMobileBehaviorProps = {
  lock: boolean;
  hideDelayMs: number;
  heightUnits: number;
  thresholdPx: number;
  velocityThreshold: number;
  velocitySmoothing: number;
  transitionSec: number;
};

export default function PublicNavbarMobileBehavior({
  lock,
  hideDelayMs,
  heightUnits,
  thresholdPx,
  velocityThreshold,
  velocitySmoothing,
  transitionSec,
}: PublicNavbarMobileBehaviorProps) {
  const lastYRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const velocityRef = useRef<number>(0);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const el = document.querySelector<HTMLElement>(
      "[data-public-navbar-mobile]",
    );
    if (!el) return;

    el.style.transform = "translateY(0)";
    el.style.opacity = "1";
    el.style.transition = `transform ${transitionSec}s ease-in-out, opacity ${transitionSec}s ease-in-out`;

    if (lock) {
      el.style.transform = "translateY(0)";
      el.style.opacity = "1";
      return;
    }

    lastYRef.current = window.scrollY || 0;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;

    const clearTimer = () => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };

    const scheduleHide = () => {
      clearTimer();
      hideTimerRef.current = window.setTimeout(() => {
        el.style.transform = "translateY(-100%)";
        el.style.opacity = "0";
        hideTimerRef.current = null;
      }, hideDelayMs);
    };

    const showNavbar = () => {
      clearTimer();
      el.style.transform = "translateY(0)";
      el.style.opacity = "1";
    };

    const handleScroll = () => {
      const now = Date.now();
      const y = window.scrollY;
      const last = lastYRef.current;
      const timeDelta = now - lastTimeRef.current;

      if (timeDelta === 0) return;

      const distance = y - last;
      const currentVelocity = Math.abs(distance) / timeDelta;

      velocityRef.current =
        velocitySmoothing * velocityRef.current +
        (1 - velocitySmoothing) * currentVelocity;

      if (distance < 0 || y < thresholdPx) {
        showNavbar();
      } else if (distance > 0 && y > thresholdPx) {
        if (velocityRef.current >= velocityThreshold) {
          scheduleHide();
        } else {
          showNavbar();
        }
      }

      lastYRef.current = y;
      lastTimeRef.current = now;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimer();
    };
  }, [
    lock,
    hideDelayMs,
    thresholdPx,
    heightUnits,
    velocityThreshold,
    velocitySmoothing,
    transitionSec,
  ]);

  return null;
}
